import React, { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { loadAutoLanguage, POPULAR_LANGUAGES } from '../i18n/autoLanguage';
import ChuyenNamHocModal from './ChuyenNamHocModal';
import {
  FiHome,
  FiUsers,
  FiBook,
  FiUserCheck,
  FiClipboard,
  FiCalendar,
  FiLogOut,
  FiGrid,
  FiChevronDown,
  FiInfo,
  FiLock,
  FiRefreshCw,
  FiGlobe
} from 'react-icons/fi';

const Layout = () => {
  const { user, logout, adminViewUser, setAdminViewUser } = useAuth();
  const navigate = useNavigate();
  const { t, i18n: i18nState } = useTranslation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showChuyenNamHoc, setShowChuyenNamHoc] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translateProgress, setTranslateProgress] = useState(0);
  const userMenuRef = useRef(null);
  const langMenuRef = useRef(null);

  const currentLang = (i18nState.language || i18n.language || 'vi').slice(0, 2);

  const menuItems = [
    { path: '/dashboard', icon: FiHome, label: t('nav.dashboard') },
    { path: '/danh-muc', icon: FiUsers, label: 'Lớp, Môn học & GV' },
    { path: '/phan-cong', icon: FiClipboard, label: t('nav.phanCong') },
    { path: '/tkb', icon: FiCalendar, label: t('nav.tkb') },
    { path: '/ket-qua-tkb', icon: FiGrid, label: t('nav.ketQuaTKB') },
  ];

  const handleUserMenuClick = (action) => {
    setUserMenuOpen(false);
    switch (action) {
      case 'profile':
        navigate('/profile');
        break;
      case 'changePassword':
        navigate('/profile?tab=password');
        break;
      case 'logout':
        logout();
        break;
      case 'chuyenNamHoc':
        setShowChuyenNamHoc(true);
        break;
      default:
        break;
    }
  };

  const handleSelectLanguage = async (langCode) => {
    setLangMenuOpen(false);
    if (langCode === i18n.language?.slice(0, 2)) return;

    if (langCode === 'vi' || langCode === 'en') {
      i18n.changeLanguage(langCode);
      return;
    }

    // Auto-translate for other languages
    setTranslating(true);
    setTranslateProgress(0);
    try {
      await loadAutoLanguage(langCode, setTranslateProgress);
      i18n.changeLanguage(langCode);
    } catch {
      // fallback to English on error
    } finally {
      setTranslating(false);
      setTranslateProgress(0);
    }
  };

  const getFlagSrc = (langCode) => {
    const map = { vi: 'vn', en: 'gb' };
    const cc = map[langCode] || POPULAR_LANGUAGES.find(l => l.code === langCode)?.countryCode || langCode;
    return `https://flagcdn.com/w40/${cc}.png`;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="layout">
      {/* Header with navigation */}
      <header className="header">
        <div className="header-left">
          <div className="logo">
            <div className="logo-icon-wrap"><FiGrid /></div>
            <div className="logo-text">
              <span className="logo-title">{t('nav.appName')}</span>
              <span className="logo-sub">Quản lý thời khóa biểu</span>
            </div>
          </div>
          <nav className="header-nav">
            {menuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              >
                <item.icon />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="header-actions">
          {/* Language Switcher */}
          <div className="lang-menu" ref={langMenuRef}>
            <button
              className="lang-menu-trigger"
              onClick={() => setLangMenuOpen(!langMenuOpen)}
              title={t('language.select')}
              disabled={translating}
            >
              {translating ? (
                <>
                  <FiGlobe style={{ animation: 'spin 1s linear infinite' }} />
                  <span style={{ fontSize: '12px' }}>{translateProgress}%</span>
                </>
              ) : (
                <img src={getFlagSrc(currentLang)} alt={currentLang}
                  style={{ height: '20px', width: 'auto', borderRadius: 3, display: 'block' }} />
              )}
            </button>
            {langMenuOpen && !translating && (
              <div className="lang-menu-dropdown">
                <button
                  className={`lang-menu-item ${currentLang === 'vi' ? 'active' : ''}`}
                  onClick={() => handleSelectLanguage('vi')}
                >
                  <img src={getFlagSrc('vi')} alt="vi" style={{ height: 16, width: 'auto', borderRadius: 2, marginRight: 6 }} />
                  {t('language.vi')}
                </button>
                <button
                  className={`lang-menu-item ${currentLang === 'en' ? 'active' : ''}`}
                  onClick={() => handleSelectLanguage('en')}
                >
                  <img src={getFlagSrc('en')} alt="en" style={{ height: 16, width: 'auto', borderRadius: 2, marginRight: 6 }} />
                  {t('language.en')}
                </button>
                <div style={{ height: '1px', background: '#f0f0f0', margin: '4px 0' }} />
                <div style={{ fontSize: '11px', color: '#999', padding: '4px 12px' }}>
                  {t('language.other')} (auto-translate)
                </div>
                {POPULAR_LANGUAGES.map(lang => (
                  <button
                    key={lang.code}
                    className={`lang-menu-item ${currentLang === lang.code ? 'active' : ''}`}
                    onClick={() => handleSelectLanguage(lang.code)}
                  >
                    <img src={getFlagSrc(lang.code)} alt={lang.code} style={{ height: 16, width: 'auto', borderRadius: 2, marginRight: 6 }} />
                    {lang.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="user-menu" ref={userMenuRef}>
            <button
              className="user-menu-trigger"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
            >
              <span>{user?.hoTen || t('nav.user')}</span>
              <FiChevronDown />
            </button>
            {userMenuOpen && (
              <div className="user-menu-dropdown">
                <button
                  className="user-menu-item"
                  onClick={() => handleUserMenuClick('profile')}
                >
                  <FiInfo />
                  {t('nav.profile')}
                </button>
                <button
                  className="user-menu-item"
                  onClick={() => handleUserMenuClick('changePassword')}
                >
                  <FiLock />
                  {t('nav.changePassword')}
                </button>
                <button
                  className="user-menu-item chuyen-nam-hoc"
                  onClick={() => handleUserMenuClick('chuyenNamHoc')}
                >
                  <FiRefreshCw />
                  {t('nav.chuyenNamHoc')}
                </button>
                <div style={{ height: '1px', background: '#f0f0f0', margin: '4px 0' }} />
                <button
                  className="user-menu-item logout"
                  onClick={() => handleUserMenuClick('logout')}
                >
                  <FiLogOut />
                  {t('nav.logout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Modal chuyển năm học */}
      {showChuyenNamHoc && (
        <ChuyenNamHocModal
          onClose={() => setShowChuyenNamHoc(false)}
          onDone={() => { setShowChuyenNamHoc(false); window.location.reload(); }}
        />
      )}

      {/* Admin: banner khi đang xem dữ liệu của người dùng khác */}
      {adminViewUser && (
        <div style={{
          background: '#fef3c7', borderBottom: '2px solid #f59e0b',
          padding: '8px 24px', display: 'flex', alignItems: 'center',
          gap: '12px', fontSize: '14px', color: '#92400e'
        }}>
          <span style={{ fontWeight: 600 }}>
            Đang xem dữ liệu của: {adminViewUser.hoTen} ({adminViewUser.email})
          </span>
          <button
            onClick={() => setAdminViewUser(null)}
            style={{
              marginLeft: 'auto', background: '#f59e0b', color: '#fff',
              border: 'none', borderRadius: '6px', padding: '4px 12px',
              cursor: 'pointer', fontWeight: 600, fontSize: '13px'
            }}
          >
            Thoát xem
          </button>
        </div>
      )}

      {/* Main Content */}
      <main className="main-content">
        <div className="page-content">
          <Outlet />
        </div>
      </main>

      <style>{`
        .lang-menu {
          position: relative;
        }
        .lang-menu-trigger {
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: 1px solid rgba(255,255,255,0.3);
          color: inherit;
          padding: 10px 8px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.15s;
          min-width: 38px;
        }
        .lang-menu-trigger:hover:not(:disabled) {
          background: rgba(255,255,255,0.15);
        }
        .lang-menu-trigger:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        .lang-menu-dropdown {
          position: absolute;
          right: 0;
          top: calc(100% + 6px);
          background: white;
          border-radius: 10px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.15);
          min-width: 180px;
          padding: 6px 0;
          z-index: 1000;
          border: 1px solid rgba(0,0,0,0.06);
        }
        .lang-menu-item {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 8px 14px;
          background: none;
          border: none;
          text-align: left;
          cursor: pointer;
          font-size: 13.5px;
          color: #374151;
          transition: background 0.1s;
        }
        .lang-menu-item:hover {
          background: #f5f5f5;
        }
        .lang-menu-item.active {
          background: #ede9fe;
          color: #6366f1;
          font-weight: 600;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Layout;
